import React, { useMemo } from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Fuse from 'fuse.js';
import { useFilteredConstructionProjects } from '@/clients/constructionProject/useConstructionProject.ts';
import { FilterOptionsConstructionProject } from '@/clients/constructionProject/types/constructionProject.ts';
import { SearchableCombobox } from '@/shared/ui/Combobox/SearchableCombobox.tsx';
import { useFormFieldSearch } from '@/shared/hooks/useFormFieldSearch.ts';
import { withDefaultSearchableComboboxOption } from '@/shared/ui/Combobox/utils/withDefaultSearchableComboboxOption.ts';

type ConstructionProject = {
    id: string;
    projectNumber: string;
    costCenter: string | null;
};

type ConstructionProjectFieldValues = {
    [key: string]: string | null;
};

export type SelectConstructionProjectInputProps = Pick<
    UseControllerProps<ConstructionProjectFieldValues, string>,
    'name' | 'defaultValue'
> & {
    organizationId: number | null;
    filters: FilterOptionsConstructionProject;
    disabled?: boolean;
    defaultValueLabel?: string;
    dataTest?: string;
    onValueChange?: (project: ConstructionProject | undefined) => void;
};

export const SelectConstructionProjectInput = React.forwardRef<HTMLInputElement, SelectConstructionProjectInputProps>(
    ({ name, defaultValue, defaultValueLabel, organizationId, filters, disabled, dataTest, onValueChange }, _ref) => {
        const { t } = useTranslation();
        const { control } = useFormContext();
        const { search, setSearch } = useFormFieldSearch(name);

        const { data, isFetching } = useFilteredConstructionProjects(organizationId, filters);
        const projects = useMemo(() => data?.constructionProjects ?? [], [data]);

        const fuse = useMemo(
            () =>
                new Fuse(projects, {
                    keys: [
                        { name: 'name', weight: 1 },
                        { name: 'description', weight: 0.5 },
                        { name: 'projectCode', weight: 1 },
                    ],
                }),
            [projects]
        );

        const options = useMemo(() => {
            const filtered = search ? fuse.search(search).map(r => r.item) : projects;
            const defaultOption =
                defaultValue && defaultValueLabel
                    ? {
                          value: defaultValue,
                          label: defaultValueLabel,
                      }
                    : undefined;

            return withDefaultSearchableComboboxOption({
                options: filtered.map(p => ({
                    value: p.id,
                    label: p.name,
                    description: p.projectCode ?? undefined,
                })),
                defaultOption,
            });
        }, [search, fuse, projects, defaultValue, defaultValueLabel]);

        return (
            <Controller
                name={name}
                defaultValue={defaultValue ?? null}
                control={control}
                render={({ field: { ref, onChange, value }, fieldState }) => (
                    <SearchableCombobox
                        fieldRef={ref}
                        label={t('selectConstructionProject.label')}
                        error={fieldState.error?.message}
                        search={search}
                        onSearchChange={setSearch}
                        loading={isFetching}
                        options={options}
                        value={value ?? undefined}
                        dataTest={dataTest}
                        onValueChange={projectId => {
                            const selectedProject = projects.find(p => p.id === projectId);

                            onChange(selectedProject?.id);
                            onValueChange?.(
                                selectedProject && {
                                    id: selectedProject.id,
                                    projectNumber: selectedProject.projectCode!,
                                    costCenter: selectedProject.costCenter ?? null,
                                }
                            );
                        }}
                        disabled={disabled || !organizationId}
                    />
                )}
            />
        );
    }
);
